import { Link } from "@tanstack/react-router";
import { useI18N } from "common/hooks";
import type { PlacesCity } from "common/types/places";

type CityLinkCardProps = {
  city: PlacesCity;
  /** Wikipedia thumbnail; undefined while loading, null when the city has none. */
  imageUrl: string | null | undefined;
};

/** A city on /places: a real link to its city page, styled like the Popular cities cards. */
export function CityLinkCard({ city, imageUrl }: CityLinkCardProps) {
  const i18n = useI18N();

  return (
    <Link
      to={`/places/${city.slug}`}
      className="flex flex-col items-center gap-2 p-3 rounded-[8px] bg-bg-brand-secondary-default hover:bg-bg-brand-secondary-hover active:bg-bg-brand-secondary-active transition-all duration-200 focus-blue w-full h-full"
    >
      <div
        aria-hidden="true"
        className="w-full aspect-square rounded-xl bg-container-neutral-minimal overflow-hidden flex items-center justify-center"
      >
        {imageUrl === undefined ? (
          <div className="w-full h-full bg-container-brand-minimal animate-pulse" />
        ) : imageUrl ? (
          <img src={imageUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          // Fixed primitives, as on Popular cities: the themed pair is the same grey in light mode.
          <div className="w-16 h-16 rounded-full bg-neutral-300 flex items-center justify-center">
            <span className="title-m-bold text-neutral-600">{city.name.charAt(0).toUpperCase()}</span>
          </div>
        )}
      </div>
      <div className="text-center w-full">
        <p className="label-m-semibold text-fg-neutral-strong">{city.name}</p>
        <p className="label-s-medium text-fg-neutral-subtle">
          {i18n.t("placesCityCount", { count: city.count })}
        </p>
      </div>
    </Link>
  );
}

export default CityLinkCard;
